import { Events, trackEvent } from '@woonstad/modules-google-tag-manager';
import clsx from 'clsx';
import { ReactElement } from 'react';

import { BoxBase } from '../box/BoxBase';
import { Tag, TagProps } from '../tag/tag';
import { Typography } from '../typography/Typography';
import { FacilityEntry, FacilityIcon, knownFacilities } from './FacilityIcon';

type Props = {
  children: ReactElement<any>;
  variant?: 'landscape' | 'square';
  image: ReactElement<any> | null;
  address: string;
  subaddress: string;
  tags?: Array<{
    text: string;
    type: TagProps['type'];
  }>;
  description: string;
  facilities?: FacilityEntry[] | null;
  trackData?: {
    event: string;
    [key: string]: unknown;
  };
}

export const WoongebouwCard = ({
  children,
  variant = 'landscape',
  image,
  address,
  subaddress,
  tags,
  description,
  facilities,
  trackData,
}: Props) => {
  const isSquare = variant === 'square';

  const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (!trackData) return;

    event.preventDefault();
    trackEvent(trackData as Events);
    window.location.href = (children.props as any).href;
  };

  return (
    <children.type
      {...(children.props as any)}
      aria-label={`${address} | ${subaddress}`}
      onClick={handleClick}
    >
      <BoxBase className="hover:shadow-box-hover">
        <div
          className={clsx('group', {
            'grid min-h-[13.375rem] grid-cols-12': !isSquare,
            'flex flex-col h-full': isSquare,
          })}
        >
          <div
            className={clsx('relative overflow-hidden', {
              'col-span-4': !isSquare,
              'shrink-0 aspect-[1.5/1]': isSquare,
            })}
          >
            {image && (
              <image.type
                {...(image.props as any)}
                className="absolute size-full object-cover"
              />
            )}

            {(tags && tags.length > 0) && (
              <div className="absolute left-0 top-0 flex flex-wrap gap-2 p-2">
                {tags.map((tag) => tag.text ? (
                  <Tag
                    key={tag.text}
                    type={tag.type}
                  >
                    {tag.text}
                  </Tag>
                ) : undefined)}
              </div>
            )}
          </div>

          <div
            className={clsx('grid gap-4 p-6', {
              'col-span-8': !isSquare,
            })}
          >
            <div
              className={clsx('flex justify-between gap-4', {
                'flex flex-col': isSquare,
              })}
            >
              <div className="flex flex-col gap-2 break-words">
                <Typography
                  variant="title-body"
                  type="h2"
                  className="text-woongebouw-card-title decoration-2 group-hover:text-card-offer-title-hover group-hover:underline"
                  data-testid="offerAddress"
                >
                  {address}
                </Typography>
                <Typography
                  variant="label-regular"
                  textColorClassname="text-woongebouw-card-subtitle"
                  type="p"
                >
                  {subaddress}
                </Typography>
              </div>
            </div>

            <div>
              <Typography
                variant="content-body"
                textColorClassname="text-woongebouw-card-description line-clamp-3"
              >
                {description}
              </Typography>
            </div>

            {(facilities && facilities.length > 0) && (
              <div className="flex flex-wrap gap-1">
                {facilities
                  .filter((facility) => knownFacilities.includes(facility.type))
                  .map((facility) => (
                    <FacilityIcon
                      key={facility.type}
                      facility={facility}
                    />
                  ))
                }
              </div>
            )}
          </div>
        </div>
      </BoxBase>
    </children.type>
  );
};
